import React, { Component, ReactNode } from 'react';
import { ConfirmPopup } from 'primereact/confirmpopup';
import CustomTable, { CustomColumnProps } from '../custom/CustomTable';
import FolderView from '../folder/view/FolderView';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import { EnrichmentVariableJsonModel } from './EnrichmentVariableJsonModel';
import LanguageUtil from '../../util/LanguageUtil';
import { EnrichmentBase } from '../../domain/enrichment/EnrichmentDomain';
import ItemStatusComponent from '../ItemStatusComponent';
import { ServiceNames } from '../../constant/ServiceNames';
import VariableType from 'constant/VariableType';
import BasicVariableService from '../../service/BasicVariableService';
import ListVariableService from 'service/ListVariableService';
import QueryVariableService from '../../service/QueryVariableService';
import ApiVariableService from '../../service/ApiVariableService';
import ServiceContainer from '../../config/ServiceContainer';
import VelocityVariableService from '../../service/VelocityVariableService';
import RoleHelper from '../../RoleHelper';
import { dispatch, RootState } from '../../redux/store';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import { connect, ConnectedProps } from 'react-redux';
import EmptyComponent from '../custom/empty-components/EmptyComponent';
import { customConfirm } from '../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact, enrichmentUndeployComponent } from '../deploy/UndeployImpactDialog';
import Icon from '../../icons/Icon';
import { query } from '@trlogic/client-js';
import { Tooltip } from 'primereact/tooltip';
import ComponentName from '../../constant/ComponentName';
import ListDraggableItem from '../custom/ListDraggableItem';
import { PermissionType } from '../../domain/rbac/RbacDomains';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import ComponentUtil from '../../util/ComponentUtil';
import moment from 'moment/moment';
import Order = query.Order;

class EnrichmentListViewerComponent extends Component<Props> {
  private readonly apiVariableService: ApiVariableService = ServiceContainer.getInstance().get(ServiceNames.API_VARIABLE);
  private readonly basicVariableService: BasicVariableService = ServiceContainer.getInstance().get(ServiceNames.BASIC_VARIABLE);
  private readonly listVariableService: ListVariableService = ServiceContainer.getInstance().get(ServiceNames.LIST_VARIABLE);
  private readonly queryVariableService: QueryVariableService = ServiceContainer.getInstance().get(ServiceNames.QUERY_VARIABLE);
  private readonly velocityVariableService: VelocityVariableService = ServiceContainer.getInstance().get(ServiceNames.VELOCITY_VARIABLE);

  render() {
    const {
      views,
      variables,
      component: { selectedComponentIds },
      onSortChange,
    } = this.props;
    return views && views.length > 0 && variables && variables.length > 0 ? (
      <div className={'overflow-y-scroll max-h-[calc(100dvh-430px)] scrollable-editor-container 2xl:max-h-[calc(100dvh-460px)] flex w-full  rounded-lg'}>
        <ConfirmPopup />
        <CustomTable
          value={variables}
          selectionMode='checkbox'
          selection={selectedComponentIds}
          onSelectionChange={this.onSelectionChange}
          rowClassName={(data) => {
            const view = views.find((v: FolderView) => v.getModel().value?.payload.name == data.name);
            return view ? `list_${view?.getModel().id}` : '';
          }}
          componentName={ComponentName.ENRICHMENT}
          openNewTab={true}
          onRowClick={this.openEditor}
          columns={this.getColumns()}
          onSortChange={onSortChange}
        />
        <ListDraggableItem itemType={ComponentName.ENRICHMENT} currentModal={this.props.currentModel} />
      </div>
    ) : (
      <EmptyComponent type={ComponentName.ENRICHMENT} />
    );
  }

  private onSelectionChange = (e: any) => {
    dispatch(setSelectedItems(e.value));
  };
  private openEditor = (e: DataTableRowClickEventParams) => {
    const { openVariable } = this.props;
    openVariable(e.data.name);
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'name',
        header: LanguageUtil.get({ id: `pages.enrichment.table.name`, defaultMessage: 'Name' }),
        className: 'min-w-[16%]',
        copyable: true,
        onCopy: (e: EnrichmentBase) => {
          return e.name;
        },
        body: (record: EnrichmentBase) => {
          const { views } = this.props;
          const view: FolderView = views.find((view: FolderView) => view.getModel().value?.payload.name == record.name);
          return (
            <div className={'flex items-start text-xs 2xl:text-md'} id={``}>
              <div id={`list_${view?.getModel().id}`} />
              <CustomTableContentWrapper clipSize={25} text={record.name} data-testid={`${ComponentName.ENRICHMENT}_name_text_${record.name}`} />
            </div>
          );
        },
      },
      {
        field: 'stage',
        header: LanguageUtil.get({ id: `pages.enrichment.table.status`, defaultMessage: 'Status' }),
        className: 'w-1/8',
        body: (record: EnrichmentBase) => {
          return (
            <ItemStatusComponent
              status={record.stage}
              containerClassName={'justify-start'}
              data-testid={`${ComponentName.ENRICHMENT}_stage_text_${record.name}`}
            />
          );
        },
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: `pages.enrichment.table.description`, defaultMessage: 'Description' }),
        className: 'min-w-[320px]',
        body: (record: EnrichmentVariableJsonModel) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper
                clipSize={52}
                text={record.description || LanguageUtil.get({ id: 'common.label.noDescriptionEnrichment', defaultMessage: 'This enrichment does not have any description yet.' })}
                data-testid={`${ComponentName.ENRICHMENT}_description_text_${record.name}`}
              />
            </div>
          );
        },
      },
      {
        field: 'transaction',
        header: LanguageUtil.get({ id: `pages.enrichment.table.transaction`, defaultMessage: 'Transaction' }),
        className: 'w-1/8',
        body: (record: any) => {
          return (
            <div className={'text-left flex items-center text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={record.transaction || ''} />
            </div>
          );
        },
      },
      {
        field: 'type',
        header: LanguageUtil.get({ id: `pages.enrichment.table.type`, defaultMessage: 'Type' }),
        className: 'w-1/8',
        body: (record: EnrichmentBase) => {
          return <div className={'flex items-center justify-start text-xs 2xl:text-md'}>{this.getEnrichmentName(record.type)}</div>;
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: `pages.enrichment.table.createdAt`, defaultMessage: 'Created At' }),
        className: 'min-w-[140px]',
        body: (record: EnrichmentBase) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.createdAt ? moment(record.createdAt).format('LLL') : ''} />
            </div>
          );
        },
      },
      {
        field: 'updatedAt',
        header: LanguageUtil.get({ id: `pages.enrichment.table.updatedAt`, defaultMessage: 'Updated At' }),
        className: 'min-w-[140px]',
        body: (record: EnrichmentBase) => {
          return (
            <div className={'flex w-full items-start text-f-black-3 text-xs 2xl:text-md justify-start relative group'}>
              <CustomTableContentWrapper text={record.updatedAt ? moment(record.updatedAt).format('LLL') : ''} />
            </div>
          );
        },
      },
    ];
    if (RoleHelper.canEdit('Enrichment')) {
      columns.push({
        field: 'action',
        header: 'Action',
        className: 'max-w-[75px] 2xl:max-w-[100px]',
        sortable: false,
        body: this.getActions,
      });
    }
    return columns;
  };

  private getActions = (record: EnrichmentBase) => {
    const { permissions } = this.props;
    const actions: ReactNode[] = [];
    const permission = permissions[record.name];
    const allowed = ['Edit', 'Admin'];
    if (!allowed.includes(permission)) {
      return null;
    }
    const isLocked = ComponentUtil.isLocked(ComponentName.ENRICHMENT, record.name);
    if (isLocked) {
      return null;
    }

    const canRemove = record.stage == 'DRAFT';
    const canUndeploy = record.stage != 'DRAFT';

    if (canUndeploy) {
      actions.push(
        <div
          key='undeploy'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () =>
                confirmUndeployWithImpact(enrichmentUndeployComponent(record.type), [record.name], () =>
                  this.getServiceFromType(record.type).undeploy(record.name),
                ),
              message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
            });
          }}
          data-testid={`${ComponentName.ENRICHMENT}_undeploy_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.undeploy'}>
            {LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          </Tooltip>
          <Icon icon={'undeploy'} size={20} className={'undeploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
        </div>,
      );
    }
    if (canRemove) {
      actions.push(
        <div
          key='remove'
          onClick={(e: any) => {
            e.stopPropagation();
            e.preventDefault();
            customConfirm({
              target: e.currentTarget,
              accept: async () => await this.getServiceFromType(record.type).delete(record.name),
              message: LanguageUtil.get({ id: 'messages.confirm.remove1', defaultMessage: 'Are you sure you want to remove?' }),
            });
          }}
          data-testid={`${ComponentName.ENRICHMENT}_remove_button_${record.name}`}
        >
          <Tooltip position={'left'} target={'.remove'}>
            {LanguageUtil.get({ id: 'common.label.remove', defaultMessage: 'Remove' })}
          </Tooltip>
          <Icon
            icon={'delete'}
            size={20}
            color={'primary'}
            className={'remove hover:text-primary text-[#607080] hover:cursor-pointer flex items-center justify-center'}
          />
        </div>,
      );
    }
    actions.push(
      <div
        key='deploy'
        onClick={(e: any) => {
          e.stopPropagation();
          e.preventDefault();
          customConfirm({
            target: e.currentTarget,
            accept: async () => await this.getServiceFromType(record.type).deploy(record as any),
            message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
          });
        }}
        data-testid={`${ComponentName.ENRICHMENT}_deploy_button_${record.name}`}
      >
        <Tooltip position={'left'} target={'.deploy'}>
          {LanguageUtil.get({ id: 'common.label.deploy', defaultMessage: 'Deploy' })}
        </Tooltip>
        <Icon icon={'deploy'} size={20} className={'deploy hover:text-primary text-[#607080] hover:cursor-pointer'} />
      </div>,
    );
    return <div className={'flex w-full justify-start items-center gap-x-4'}>{actions}</div>;
  };

  private getServiceFromType(type: VariableType) {
    switch (type) {
      case VariableType.LIST:
        return this.listVariableService;
      case VariableType.QUERY:
        return this.queryVariableService;
      case VariableType.VELOCITY:
        return this.velocityVariableService;
      case VariableType.BASIC:
        return this.basicVariableService;
      case VariableType.API:
        return this.apiVariableService;
      default:
        return this.basicVariableService;
    }
  }

  private getEnrichmentName(type: VariableType): String {
    switch (type) {
      case VariableType.LIST:
        return 'List';
      case VariableType.QUERY:
        return 'Query';
      case VariableType.VELOCITY:
        return 'Velocity';
      case VariableType.BASIC:
        return 'Basic';
      case VariableType.API:
        return 'Api';
      default:
        return 'Enrichment Variable(This variable probably from an old version)';
    }
  }
}

interface Props extends ReduxProps {
  variables: EnrichmentBase[];
  openVariable: (enrichment: string) => void;
  currentModel: any;
  views: any;
  onSortChange: (sort: Order[]) => void;
  permissions: Record<string, PermissionType>;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component as ComponentState,
});
const mapDispatchToProps = {};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(EnrichmentListViewerComponent);
